import React, { useCallback, useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ProjectRequestSheet from "@/components/ProjectRequestSheet";
import { Button } from "@/components/ui/button";
import { CaretLeft, CaretRight, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

const DAY_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function mondayOf(d) {
    const x = new Date(d);
    const day = (x.getDay() + 6) % 7;
    x.setDate(x.getDate() - day);
    x.setHours(0, 0, 0, 0);
    return x;
}

function isoDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function fmtRange(monday) {
    const end = new Date(monday);
    end.setDate(end.getDate() + 6);
    const opt = { day: "numeric", month: "short" };
    return `${monday.toLocaleDateString("es-ES", opt)} — ${end.toLocaleDateString("es-ES", { ...opt, year: "numeric" })}`;
}

export default function Timesheet() {
    const [monday, setMonday] = useState(mondayOf(new Date()));
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState({}); // key `${assignment_id}|${date}` -> hours string

    const load = useCallback(async () => {
        const res = await http.get("/timesheet", {
            params: { week_start: isoDate(monday) },
        });
        setData(res.data);
        setDraft({});
    }, [monday]);

    useEffect(() => {
        load();
    }, [load]);

    const days = data?.days || [];
    const customers = data?.customers || [];
    const projectsByCustomer = useMemo(() => {
        const m = {};
        if (!data) return m;
        const projMap = Object.fromEntries(data.projects.map((p) => [p.project_id, p]));
        for (const a of data.assignments) {
            const p = projMap[a.project_id];
            if (!p) continue;
            const list = m[p.customer_id] || [];
            list.push({ ...p, assignment_id: a.assignment_id, editable: a.editable !== false });
            m[p.customer_id] = list;
        }
        return m;
    }, [data]);

    const entryMap = useMemo(() => {
        const m = {};
        if (!data) return m;
        for (const e of data.entries) {
            m[`${e.assignment_id}|${e.entry_date}`] = e.hours;
        }
        return m;
    }, [data]);

    const cellValue = (asgId, date) => {
        const k = `${asgId}|${date}`;
        return draft[k] !== undefined ? draft[k] : (entryMap[k] ?? "");
    };

    const setCellDraft = (asgId, date, value) => {
        const k = `${asgId}|${date}`;
        setDraft((prev) => ({ ...prev, [k]: value }));
    };

    const saveCell = async (assignment_id, date) => {
        const k = `${assignment_id}|${date}`;
        const raw = draft[k];
        if (raw === undefined) return;
        const value = String(raw).replace(",", ".").trim();
        const hours = value === "" ? 0 : Number(value);
        if (Number.isNaN(hours) || hours < 0 || hours > 24) {
            toast.error("Valor inválido (0–24 horas)");
            return;
        }
        try {
            await http.post("/timesheet/cell", {
                assignment_id,
                entry_date: date,
                hours,
            });
            await load();
            toast.success("Guardado");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error al guardar");
        }
    };

    const totalsPerDay = useMemo(() => {
        const totals = days.map(() => 0);
        if (!data) return totals;
        for (const c of customers) {
            for (const p of projectsByCustomer[c.customer_id] || []) {
                days.forEach((d, i) => {
                    const v = Number(cellValue(p.assignment_id, d) || 0);
                    if (!Number.isNaN(v)) totals[i] += v;
                });
            }
        }
        return totals;
    }, [data, days, customers, projectsByCustomer, draft, entryMap]);

    const grandTotal = totalsPerDay.reduce((a, b) => a + b, 0);

    return (
        <div data-testid="page-timesheet">
            <PageHeader
                eyebrow="Parte semanal"
                title="Parte de horas"
                description="Imputa horas por proyecto. Pulsa Tab o haz clic fuera de la celda para guardar automáticamente."
                actions={
                    <div className="flex items-center gap-2">
                        <ProjectRequestSheet onCreated={load} />
                        <Button
                            variant="outline"
                            className="rounded-sm"
                            onClick={() => {
                                const m = new Date(monday);
                                m.setDate(m.getDate() - 7);
                                setMonday(m);
                            }}
                            data-testid="ts-prev-week"
                        >
                            <CaretLeft size={14} />
                        </Button>
                        <Button
                            variant="outline"
                            className="rounded-sm font-mono text-xs"
                            onClick={() => setMonday(mondayOf(new Date()))}
                            data-testid="ts-this-week"
                        >
                            {fmtRange(monday)}
                        </Button>
                        <Button
                            variant="outline"
                            className="rounded-sm"
                            onClick={() => {
                                const m = new Date(monday);
                                m.setDate(m.getDate() + 7);
                                setMonday(m);
                            }}
                            data-testid="ts-next-week"
                        >
                            <CaretRight size={14} />
                        </Button>
                    </div>
                }
            />

            <div className="p-6 lg:p-10">
                <div className="border border-slate-200 bg-white overflow-x-auto">
                    <table className="w-full text-sm" data-testid="timesheet-grid">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold w-1/3">
                                    Cliente / Proyecto
                                </th>
                                {days.map((d, i) => {
                                    const dt = new Date(d);
                                    const isWeekend = i >= 5;
                                    return (
                                        <th
                                            key={d}
                                            className={`text-center py-3 px-3 text-xs font-semibold uppercase tracking-wider ${isWeekend ? "text-slate-400" : "text-slate-500"}`}
                                        >
                                            {DAY_LABELS[i]}
                                            <div className="text-[10px] font-mono mt-0.5 text-slate-400">
                                                {dt.getDate()}/{dt.getMonth() + 1}
                                            </div>
                                        </th>
                                    );
                                })}
                                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                                    Total
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {customers.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={9}
                                        className="text-center py-12 text-slate-500 text-sm"
                                    >
                                        No tienes proyectos asignados. Pide a un
                                        administrador que te asigne proyectos.
                                    </td>
                                </tr>
                            )}
                            {customers.map((c) => {
                                const projs = projectsByCustomer[c.customer_id];
                                if (!projs || !projs.length) return null;
                                return (
                                    <React.Fragment key={`c-${c.customer_id}`}>
                                        <tr
                                            className="bg-slate-50/50 border-y border-slate-200"
                                        >
                                            <td
                                                colSpan={9}
                                                className="py-2 px-4 text-[11px] uppercase tracking-[0.15em] text-slate-600 font-semibold"
                                            >
                                                {c.name}
                                            </td>
                                        </tr>
                                        {projs.map((p) => {
                                            const rowTotal = days.reduce(
                                                (s, d) =>
                                                    s +
                                                    Number(
                                                        cellValue(p.assignment_id, d) || 0,
                                                    ),
                                                0,
                                            );
                                            return (
                                                <tr
                                                    key={p.assignment_id}
                                                    className="border-b border-slate-100 hover:bg-slate-50"
                                                    data-testid={`ts-row-${p.project_id}`}
                                                >
                                                    <td className="py-2 px-4 text-slate-800">
                                                        <div className="font-medium flex items-center gap-2">
                                                            {p.name}
                                                            {p.editable === false && (
                                                                <span
                                                                    className="text-[9px] uppercase tracking-wider bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded-sm font-semibold"
                                                                    data-testid={`ts-readonly-badge-${p.project_id}`}
                                                                >
                                                                    Archivado · solo lectura
                                                                </span>
                                                            )}
                                                        </div>
                                                        {p.project_code && (
                                                            <div className="text-[11px] text-slate-400 font-mono">
                                                                {p.project_code}
                                                            </div>
                                                        )}
                                                    </td>
                                                    {days.map((d) => {
                                                        const v = cellValue(
                                                            p.assignment_id,
                                                            d,
                                                        );
                                                        const readOnly = p.editable === false;
                                                        return (
                                                            <td
                                                                key={d}
                                                                className="py-1 px-1 text-center"
                                                            >
                                                                <input
                                                                    className={`ts-cell ${!v ? "is-zero" : ""} ${readOnly ? "is-readonly" : ""}`}
                                                                    value={v || ""}
                                                                    placeholder="0"
                                                                    disabled={readOnly}
                                                                    readOnly={readOnly}
                                                                    onChange={(e) =>
                                                                        setCellDraft(
                                                                            p.assignment_id,
                                                                            d,
                                                                            e.target
                                                                                .value,
                                                                        )
                                                                    }
                                                                    onBlur={() =>
                                                                        saveCell(
                                                                            p.assignment_id,
                                                                            d,
                                                                        )
                                                                    }
                                                                    onKeyDown={(e) => {
                                                                        if (
                                                                            e.key ===
                                                                            "Enter"
                                                                        ) {
                                                                            e.target.blur();
                                                                        }
                                                                    }}
                                                                    data-testid={`ts-cell-${p.project_id}-${d}`}
                                                                />
                                                            </td>
                                                        );
                                                    })}
                                                    <td className="py-2 px-4 text-right font-mono tabular-nums text-slate-900 font-semibold">
                                                        {rowTotal.toFixed(2)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="border-t-2 border-slate-300 bg-slate-50">
                                <td className="py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">
                                    Total diario
                                </td>
                                {totalsPerDay.map((t, i) => (
                                    <td
                                        key={i}
                                        className="py-3 px-3 text-center font-mono tabular-nums text-slate-900 font-semibold"
                                    >
                                        {t.toFixed(2)}
                                    </td>
                                ))}
                                <td
                                    className="py-3 px-4 text-right font-mono tabular-nums text-slate-950 font-bold text-base"
                                    data-testid="ts-grand-total"
                                >
                                    {grandTotal.toFixed(2)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <div className="mt-4 text-xs text-slate-500 flex items-center gap-2">
                    <FloppyDisk size={14} />
                    Los cambios se guardan automáticamente al salir de cada celda.
                </div>
            </div>
        </div>
    );
}
